"use client"
import React from 'react'
import HeaderMenu from '@/app/components/HeaderMenu'
import { BlurFade } from '@/app/components/BlurFade'
import { motion } from "motion/react"
import ParticleField from './ParticleField'
import { RedirectToMM } from '@/app/components/DeviceRedirect'
import type { Locale } from '@/lib/i18n'
import type { CooperateDict } from '@/lib/i18n/dicts/cooperate'

// 平台优势图标（与 dict.advantages.items 顺序对应，纯装饰性 SVG 路径，不入字典）
const ADV_ICONS = [
    'M20 7h-9m9 10h-9m-7-5h.01M7 7h.01M5 3h14a2 2 0 012 2v14a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2z',
    'M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
    'M10.5 1.5H8.25A2.25 2.25 0 006 3.75v16.5A2.25 2.25 0 008.25 22.5h7.5A2.25 2.25 0 0018 20.25V3.75a2.25 2.25 0 00-2.25-2.25H13.5m-3 0V3h3V1.5m-3 0h3m-3 18.75h3',
    'M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z',
]

interface Cooperate2ClientProps {
    dict: CooperateDict
    locale: Locale
    // 页脚为服务端组件（读库统计），由服务端页面渲染后传入
    footer: React.ReactNode
}

export default function Cooperate2Client({ dict, locale, footer }: Cooperate2ClientProps) {
    const t = dict
    return (
        <>
            <RedirectToMM href="/mm/contact" />
            <HeaderMenu locale={locale} />

            {/* Hero */}
            <section className="relative overflow-hidden bg-slate-950">
                <div className="pointer-events-none absolute -top-48 left-1/2 h-[520px] w-[820px] -translate-x-1/2 rounded-full bg-purple-600/20 blur-[130px]" />
                <div className="pointer-events-none absolute top-24 right-[-140px] h-[340px] w-[340px] rounded-full bg-indigo-500/10 blur-[110px]" />

                {/* 粒子网络动效背景 */}
                <ParticleField className="opacity-70 [mask-image:radial-gradient(ellipse_70%_80%_at_50%_40%,black,transparent)]" />

                <div className="relative max-w-7xl mx-auto px-4 md:px-8 pt-20 pb-16 text-center">
                    <BlurFade delay={0.05} inView>
                        <span className="inline-flex items-center gap-2 rounded-full ring-1 ring-purple-400/30 bg-purple-500/10 px-4 py-1.5 text-xs font-medium tracking-widest text-purple-300 uppercase">
                            <span className="h-1.5 w-1.5 rounded-full bg-purple-400 animate-pulse" />
                            {t.hero.badge}
                        </span>
                    </BlurFade>
                    <BlurFade delay={0.15} inView>
                        <h1 className="mt-6 text-4xl md:text-6xl font-black tracking-tight text-white">
                            {t.hero.titleA}
                            <span className="bg-gradient-to-r from-purple-400 via-fuchsia-400 to-indigo-300 bg-clip-text text-transparent">{t.hero.titleB}</span>
                        </h1>
                    </BlurFade>
                    <BlurFade delay={0.25} inView>
                        <p className="mt-5 max-w-2xl mx-auto text-sm md:text-base leading-relaxed text-slate-400">
                            {t.hero.subtitle}
                        </p>
                    </BlurFade>
                    <BlurFade delay={0.35} inView>
                        <div className="mt-8 flex justify-center gap-4">
                            <a href="../contact2" className="rounded-full bg-gradient-to-r from-purple-600 to-indigo-600 px-8 py-3 text-sm font-bold text-white shadow-lg shadow-purple-600/30 transition-transform duration-300 hover:scale-105">
                                {t.hero.ctaPrimary}
                            </a>
                            <a href="#plans" className="rounded-full ring-1 ring-white/20 px-8 py-3 text-sm font-medium text-slate-200 transition-colors duration-300 hover:bg-white/5">
                                {t.hero.ctaSecondary}
                            </a>
                        </div>
                    </BlurFade>
                </div>
            </section>

            {/* 合作模式 */}
            <section id="plans" className="bg-slate-950 py-20">
                <div className="max-w-7xl mx-auto px-4 md:px-8">
                    <BlurFade inView>
                        <h2 className="text-center text-2xl md:text-3xl font-bold text-white">{t.plans.title}</h2>
                        <p className="mt-3 text-center text-sm text-slate-400">{t.plans.subtitle}</p>
                    </BlurFade>

                    {/* 限时活动横幅：新商户免费试跑 15 天 */}
                    <BlurFade delay={0.1} inView>
                        <div className="mt-8 mx-auto max-w-3xl rounded-2xl bg-gradient-to-r from-amber-500/15 via-orange-500/10 to-amber-500/5 ring-1 ring-amber-400/30 px-6 py-4 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-5">
                            <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-500 px-3 py-1 text-[11px] font-black tracking-wider text-slate-950 shadow-lg shadow-amber-500/30">
                                {t.plans.campaign.badge}
                            </span>
                            <p className="text-center sm:text-left">
                                <span className="text-base font-bold text-amber-200">{t.plans.campaign.title}</span>
                                <span className="ml-2 text-xs text-amber-200/60">{t.plans.campaign.desc}</span>
                            </p>
                            <a href="../contact2"
                                className="shrink-0 rounded-full bg-amber-400 px-5 py-1.5 text-xs font-bold text-slate-950 transition-all duration-300 hover:bg-amber-300 hover:scale-105">
                                {t.plans.campaign.button}
                            </a>
                        </div>
                    </BlurFade>

                    <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch">
                        {t.plans.items.map((plan, i) => (
                            <motion.div
                                key={plan.name}
                                initial={{ opacity: 0, y: 32 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-60px" }}
                                transition={{ duration: 0.5, delay: i * 0.12 }}
                                className={`relative flex flex-col rounded-2xl p-7 ring-1 transition-all duration-300 hover:-translate-y-2 ${plan.highlight
                                        ? 'bg-gradient-to-b from-indigo-600/20 to-slate-900 ring-indigo-400/60 shadow-2xl shadow-indigo-600/20 md:scale-[1.04]'
                                        : 'bg-slate-900 ring-white/10 hover:ring-white/25'
                                    }`}
                            >
                                {plan.badge && (
                                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-gradient-to-r from-indigo-500 to-fuchsia-500 px-4 py-1 text-xs font-bold text-white shadow-lg shadow-indigo-600/40">
                                        {plan.badge}
                                    </span>
                                )}
                                <p className="text-xs font-medium tracking-widest text-slate-500 uppercase">{plan.en}</p>
                                <h3 className="mt-1 text-2xl font-bold text-white">{plan.name}</h3>
                                <p className="mt-2 text-xs leading-5 text-slate-400 min-h-[2.5rem]">{plan.desc}</p>

                                <ul className="mt-6 space-y-3.5 flex-1">
                                    {plan.features.map((f) => (
                                        <li key={f.text} className="flex items-center gap-2.5">
                                            <span className={`rounded-full p-0.5 ${f.ok ? 'bg-indigo-500' : 'bg-slate-700'}`}>
                                                {f.ok ? (
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 text-white" viewBox="0 0 20 20" fill="currentColor">
                                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                    </svg>
                                                ) : (
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 text-slate-500" viewBox="0 0 20 20" fill="currentColor">
                                                        <path fillRule="evenodd" d="M5 10a1 1 0 011-1h8a1 1 0 110 2H6a1 1 0 01-1-1z" clipRule="evenodd" />
                                                    </svg>
                                                )}
                                            </span>
                                            <span className={`text-sm ${f.ok ? 'text-slate-200' : 'text-slate-600'}`}>{f.text}</span>
                                        </li>
                                    ))}
                                </ul>

                                <a
                                    href="../contact2"
                                    className={`mt-8 block rounded-full py-2.5 text-center text-sm font-bold transition-all duration-300 ${plan.highlight
                                            ? 'bg-gradient-to-r from-indigo-500 to-fuchsia-500 text-white shadow-lg shadow-indigo-600/30 hover:scale-[1.03]'
                                            : 'ring-1 ring-white/20 text-slate-200 hover:bg-white/5'
                                        }`}
                                >
                                    {t.plans.contact}
                                </a>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* 合作流程 */}
            {/* 四步开启合作：与上下文同底色（slate-950），卡片提亮一档保持层次 */}
            <section className="bg-slate-950 py-20">
                <div className="max-w-7xl mx-auto px-4 md:px-8">
                    <BlurFade inView>
                        <h2 className="text-center text-2xl md:text-3xl font-bold text-white">{t.steps.title}</h2>
                        <p className="mt-3 text-center text-sm text-slate-400">{t.steps.subtitle}</p>
                    </BlurFade>

                    <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        {t.steps.items.map((step, i) => (
                            <motion.div
                                key={step.title}
                                initial={{ opacity: 0, y: 24 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-60px" }}
                                transition={{ duration: 0.5, delay: i * 0.1 }}
                                className="relative rounded-2xl bg-slate-900 ring-1 ring-white/10 p-6 transition-all duration-300 hover:ring-purple-400/50 hover:-translate-y-1"
                            >
                                <span className="text-4xl font-black bg-gradient-to-br from-purple-400 to-indigo-500 bg-clip-text text-transparent">
                                    {String(i + 1).padStart(2, '0')}
                                </span>
                                <h3 className="mt-3 text-lg font-bold text-white">{step.title}</h3>
                                <p className="mt-2 text-xs leading-5 text-slate-400">{step.desc}</p>
                                {i < t.steps.items.length - 1 && (
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="hidden lg:block absolute top-8 -right-4 h-5 w-5 text-slate-600">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
                                    </svg>
                                )}
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* 平台优势 */}
            <section className="bg-slate-950 py-20">
                <div className="max-w-7xl mx-auto px-4 md:px-8">
                    <BlurFade inView>
                        <h2 className="text-center text-2xl md:text-3xl font-bold text-white">{t.advantages.title}</h2>
                    </BlurFade>

                    <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        {t.advantages.items.map((adv, i) => (
                            <motion.div
                                key={adv.title}
                                initial={{ opacity: 0, y: 24 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-60px" }}
                                transition={{ duration: 0.5, delay: i * 0.1 }}
                                className="group rounded-2xl bg-slate-900 ring-1 ring-white/10 p-6 transition-all duration-300 hover:ring-indigo-400/50 hover:-translate-y-1 hover:shadow-xl hover:shadow-indigo-600/10"
                            >
                                <div className="inline-flex rounded-xl bg-indigo-500/10 ring-1 ring-indigo-400/30 p-3 text-indigo-400 transition-colors duration-300 group-hover:text-indigo-300">
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-6 w-6">
                                        <path strokeLinecap="round" strokeLinejoin="round" d={ADV_ICONS[i]} />
                                    </svg>
                                </div>
                                <h3 className="mt-4 text-base font-bold text-white">{adv.title}</h3>
                                <p className="mt-2 text-xs leading-5 text-slate-400">{adv.desc}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="relative overflow-hidden bg-gradient-to-r from-purple-700 via-indigo-700 to-purple-700 py-16">
                <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(255,255,255,0.08),transparent_60%)]" />
                <div className="relative max-w-3xl mx-auto px-4 text-center">
                    <BlurFade inView>
                        <h2 className="text-2xl md:text-4xl font-black text-white">{t.cta.title}</h2>
                        <p className="mt-4 text-sm md:text-base text-indigo-100">
                            {t.cta.desc}
                        </p>
                        <a href="../contact2" className="mt-8 inline-block rounded-full bg-white px-10 py-3.5 text-sm font-bold text-indigo-700 shadow-xl transition-transform duration-300 hover:scale-105">
                            {t.cta.button}
                        </a>
                    </BlurFade>
                </div>
            </section>

            {footer}
        </>
    )
}
